import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { inr, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Zap, Package, Layers, Boxes, Clock, Check, ShieldCheck, Download, ArrowRight, Loader2 } from "lucide-react";

const TYPE_LABEL = { DIGITAL: "Digital Product", PHYSICAL: "Physical Product", HYBRID: "Digital + Physical", BUNDLE: "Bundle" };
const TYPE_ICON = { DIGITAL: Zap, PHYSICAL: Package, HYBRID: Layers, BUNDLE: Boxes };

function NotifyDialog({ product }) {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", email_consent: true, whatsapp_consent: false });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const submit = async () => {
    if (!form.email) return toast.error("Email is required");
    setLoading(true);
    try {
      const { data } = await api.post("/upcoming/notify", { product_id: product.id, ...form });
      toast.success(data.message); setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" className="w-full bg-navy hover:bg-navy-700 text-white" data-testid="notify-open-btn">Notify Me at Launch</Button>
      </DialogTrigger>
      <DialogContent data-testid="notify-dialog">
        <DialogHeader><DialogTitle>Get notified — {product.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="notify-name" />
          <Input type="email" placeholder="Email address*" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="notify-email" />
          <Input placeholder="Mobile / WhatsApp (optional)" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="notify-mobile" />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Checkbox checked={form.email_consent} onCheckedChange={(v) => setForm({ ...form, email_consent: !!v })} data-testid="notify-email-consent" />
            Notify me by email
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Checkbox checked={form.whatsapp_consent} onCheckedChange={(v) => setForm({ ...form, whatsapp_consent: !!v })} data-testid="notify-wa-consent" />
            Notify me on WhatsApp
          </label>
          <Button onClick={submit} disabled={loading} className="w-full bg-teal hover:bg-teal-dark text-white" data-testid="notify-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Notify Me"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { data: p, isLoading, error } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => (await api.get(`/products/${slug}`)).data,
  });

  if (isLoading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>;
  if (error) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><h2 className="text-xl font-bold text-navy">Product not found</h2><Button asChild className="mt-4"><Link to="/shop">Back to shop</Link></Button></div>;

  const isUpcoming = p.upcoming || ["UPCOMING", "PRE_LAUNCH"].includes(p.status);
  const Icon = TYPE_ICON[p.type] || Zap;
  const buyNow = () => { add(p); navigate("/checkout"); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <nav className="text-sm text-slate-400 mb-5"><Link to="/shop" className="hover:text-teal">Shop</Link> / <span className="text-slate-600">{p.name}</span></nav>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
            {p.cover_image && <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover" />}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-teal-50 text-teal-700 border border-teal-200 gap-1"><Icon className="w-3 h-3" /> {isUpcoming ? "Coming Soon" : TYPE_LABEL[p.type]}</Badge>
            {p.on_offer && <Badge className="bg-gold text-white">Launch Offer</Badge>}
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-navy leading-tight">{p.name}</h1>
          <p className="mt-3 text-slate-600">{p.short_description}</p>

          {isUpcoming ? (
            <div className="mt-6 bg-navy/5 border border-navy/10 rounded-xl p-5">
              <div className="flex items-center gap-2 text-navy font-semibold"><Clock className="w-4 h-4" /> Launching {p.launch_date ? new Date(p.launch_date).toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "soon"}</div>
              <p className="text-sm text-slate-500 mt-2">This product isn't available yet. Leave your details and we'll notify you the moment it launches.</p>
            </div>
          ) : (
            <div className="mt-6 flex items-baseline gap-3">
              <span className="text-3xl sm:text-4xl font-extrabold text-navy" data-testid="detail-price">{inr(p.price)}</span>
              {p.compare_at && <span className="text-lg text-slate-400 line-through">{inr(p.compare_at)}</span>}
              {p.compare_at && <Badge className="bg-teal-50 text-teal-700 border border-teal-200">Save {inr(p.compare_at - p.price)}</Badge>}
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {isUpcoming ? <div className="flex-1"><NotifyDialog product={p} /></div> : (
              <>
                <Button size="lg" variant="outline" className="flex-1 border-slate-300" onClick={() => add(p)} data-testid="detail-add-cart">Add to Cart</Button>
                <Button size="lg" className="flex-1 bg-teal hover:bg-teal-dark text-white gap-2" onClick={buyNow} data-testid="detail-buy-now">Buy Now <ArrowRight className="w-4 h-4" /></Button>
              </>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500">
            {p.type !== "PHYSICAL" && <span className="flex items-center gap-1.5"><Download className="w-4 h-4 text-teal" /> Instant digital access after payment</span>}
            {p.requires_shipping && <span className="flex items-center gap-1.5"><Package className="w-4 h-4 text-teal" /> Shipped to your address</span>}
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-teal" /> Secure Razorpay checkout</span>
          </div>

          {p.bundle_products?.length > 0 && (
            <div className="mt-8 bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-heading font-semibold text-navy mb-3">What's inside this bundle</h3>
              <ul className="space-y-2">
                {p.bundle_products.map((bp) => (
                  <li key={bp.id} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-700"><Check className="w-4 h-4 text-teal" /> {bp.name}</span>
                    <span className="text-slate-400">{inr(bp.price)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Description / What you get */}
      <div className="grid lg:grid-cols-3 gap-8 mt-12">
        <div className="lg:col-span-2 space-y-8">
          {p.full_description && (
            <div><h2 className="text-xl font-bold text-navy mb-3">About this product</h2>
              <p className="text-slate-600 leading-relaxed">{p.full_description}</p></div>
          )}
          {p.what_you_get?.length > 0 && (
            <div><h2 className="text-xl font-bold text-navy mb-3">What you get</h2>
              <ul className="grid sm:grid-cols-2 gap-2">
                {p.what_you_get.map((w, i) => <li key={i} className="flex gap-2 items-start text-slate-700 text-sm"><Check className="w-4 h-4 text-teal mt-0.5 shrink-0" /> {w}</li>)}
              </ul></div>
          )}
          {p.faq?.length > 0 && (
            <div><h2 className="text-xl font-bold text-navy mb-3">Product FAQ</h2>
              <Accordion type="single" collapsible>
                {p.faq.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left text-navy">{f.q}</AccordionTrigger><AccordionContent className="text-slate-600">{f.a}</AccordionContent></AccordionItem>
                ))}
              </Accordion></div>
          )}
        </div>
        <div className="space-y-5">
          {p.who_its_for?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-heading font-semibold text-navy mb-3">Who it's for</h3>
              <ul className="space-y-2">{p.who_its_for.map((w, i) => <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-teal">•</span> {w}</li>)}</ul>
            </div>
          )}
          {p.product_contents?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-heading font-semibold text-navy mb-3">Product contents</h3>
              <ul className="space-y-2">{p.product_contents.map((w, i) => <li key={i} className="text-sm text-slate-600 flex gap-2"><Check className="w-4 h-4 text-teal shrink-0" /> {w}</li>)}</ul>
            </div>
          )}
        </div>
      </div>

      {p.related?.length > 0 && (
        <div className="mt-14">
          <h2 className="text-xl font-bold text-navy mb-6">Related products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {p.related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}
